import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, interval, of, Subscription } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { NotificationControllerService } from 'projects/api-swagger-client/src';
export interface NotificationStats {
    total: number;
    sent: number;
    failed: number;
    unread: number;
}
export interface NotificationLog {
    id: number;
    userId: number;
    fcmToken: string;
    title: string;
    body: string;
    imageUrl?: string;
    data?: any;
    status: 'queued' | 'sent' | 'failed' | 'read';
    attempts: number;
    fcmMessageId?: string;
    errorMessage?: string;
    sentAt?: Date;
    readAt?: Date;
    lastAttemptAt?: Date;
    deleted: boolean;
    createdOn: Date;
    modifiedOn: Date;
    createdBy?: number;
    modifiedBy?: number;
}

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    type = 'web';

    // BehaviorSubject para notificaciones no leídas
    private unreadCountSubject = new BehaviorSubject<number>(0);
    public unreadCount$ = this.unreadCountSubject.asObservable();
    private audio = new Audio();

    // Polling interval (30 segundos)
    private readonly POLL_INTERVAL = 30000;
    private pollingSubscription?: Subscription;

    constructor(
        private notificationControllerService: NotificationControllerService
    ) {
        // Iniciar polling automático
        this.startPolling();
        this.audio.src = 'assets/notification-sound.mp3';
        this.audio.load();
    }

    /**
     * Obtener mis notificaciones con paginación
     */
    getMyNotifications(skip: number = 0, limit: number = 20): Observable<any> {
        return this.notificationControllerService
            .notificationControllerGetMyNotifications(skip, limit)
            .pipe(
                map((response: any) => response || []),
                catchError((error) => {
                    console.error('Error fetching notifications:', error);
                    return of([]);
                })
            );
    }

    /**
     * Obtener notificaciones no leídas
     */
    getUnreadNotifications(limit: number = 50): Observable<NotificationLog[]> {
        return this.notificationControllerService
            .notificationControllerGetUnreadNotifications(limit, this.type)
            .pipe(
                map((response: any) => response || []),
                catchError((error) => {
                    console.error(
                        'Error fetching unread notifications:',
                        error
                    );
                    return of([]);
                })
            );
    }

    /**
     * Contar notificaciones no leídas
     */
    countUnreadNotifications(): Observable<number> {
        return this.notificationControllerService
            .notificationControllerCountUnreadNotifications(this.type)
            .pipe(
                map((response: any) => {
                    // La respuesta es directamente { count: 3 }
                    const count = response?.count || 0;
                    return count;
                }),
                tap((count) => this.unreadCountSubject.next(count)),
                catchError((error) => {
                    console.error('Error al contar notificaciones:', error);
                    this.unreadCountSubject.next(0);
                    return [0];
                })
            );
    }

    /**
     * Obtener una notificación por ID
     */
    getNotificationById(id: number): Observable<NotificationLog> {
        return this.notificationControllerService
            .notificationControllerGetNotificationById(id, {})
            .pipe(map((response: any) => response.data));
    }

    /**
     * Marcar una notificación como leída
     */
    markAsRead(id: number): Observable<any> {
        return this.notificationControllerService
            .notificationControllerMarkAsRead(id)
            .pipe(tap(() => this.refreshUnreadCount()));
    }

    /**
     * Marcar todas las notificaciones como leídas
     */
    markAllAsRead(): Observable<any> {
        return this.notificationControllerService
            .notificationControllerMarkAllAsRead()
            .pipe(tap(() => this.refreshUnreadCount()));
    }

    /**
     * Reenviar mis notificaciones fallidas
     */
    resendMyFailedNotifications(): Observable<any> {
        return this.notificationControllerService.notificationControllerResendMyFailedNotifications();
    }

    /**
     * Obtener mis estadísticas
     */
    getMyStats(): Observable<NotificationStats> {
        return this.notificationControllerService
            .notificationControllerGetMyStats()
            .pipe(
                // El endpoint devuelve el objeto plano (sin envelope `data`)
                map((response: any) => {
                    const stats = response?.data ?? response ?? {};
                    return {
                        total: stats.total || 0,
                        sent: stats.sent || 0,
                        failed: stats.failed || 0,
                        unread: stats.unread || 0,
                    };
                }),
                catchError((error) => {
                    console.error('Error al obtener estadísticas:', error);
                    return of({ total: 0, sent: 0, failed: 0, unread: 0 });
                })
            );
    }

    /**
     * Refrescar el contador de no leídas
     */
    refreshUnreadCount(): void {
        this.countUnreadNotifications().subscribe();
    }

    /**
     * Iniciar polling automático del contador
     */
    private startPolling(): void {
        // Primera carga inmediata
        this.refreshUnreadCount();

        // Polling cada 30 segundos
        this.pollingSubscription = interval(this.POLL_INTERVAL).subscribe(() => {
            this.refreshUnreadCount();
        });
    }

    /**
     * Detener el polling (útil para cleanup)
     */
    stopPolling(): void {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = undefined;
        }
    }

    /**
     * Enviar notificación individual
     */
    sendNotification(payload: {
        userId: number;
        fcmToken: string;
        title: string;
        body: string;
        imageUrl?: string;
        data?: object;
    }): Observable<any> {
        return this.notificationControllerService
            .notificationControllerSendNotification(payload)
            .pipe(
                tap((response: any) => {
                    if (response.success) {
                        // Refrescar el contador después de enviar
                        this.refreshUnreadCount();
                    }
                }),
                catchError((error) => {
                    console.error('Error al enviar notificación:', error);
                    throw error;
                })
            );
    }

    /**
     * Enviar notificaciones múltiples
     */
    sendMultipleNotifications(payload: {
        recipients: Array<{ userId: number; token?: string }>;
        title: string;
        body: string;
        imageUrl?: string;
        data?: object;
    }): Observable<any> {
        return this.notificationControllerService
            .notificationControllerSendMultipleNotifications(payload)
            .pipe(
                tap((response: any) => {
                    if (response.success) {
                      
                        this.refreshUnreadCount();
                    }
                }),
                catchError((error) => {
                    console.error(
                        'Error al enviar notificaciones múltiples:',
                        error
                    );
                    throw error;
                })
            );
    }

    /**
     * Enviar notificación a todos los usuarios (Admin)
     */
    sendToAllNotifications(payload: {
        title: string;
        body: string;
        imageUrl?: string;
        data?: object;
    }): Observable<any> {
        return this.notificationControllerService
            .notificationControllerSendAllNotifications(payload)
            .pipe(
                tap((response: any) => {
                    if (response.success) {
                        this.refreshUnreadCount();
                    }
                }),
                catchError((error) => {
                    console.error('Error al enviar notificación global:', error);
                    throw error;
                })
            );
    }

    /**
     * Obtener todas las notificaciones en el sistema (Admin)
     */
    getAllNotifications(
        skip: number = 0,
        limit: number = 20,
        order: string[] = ['createdOn DESC']
    ): Observable<NotificationLog[]> {
        // IMPORTANTE: el cliente generado serializa un objeto perdiendo la llave
        // `filter` (queda ?skip=..&limit=..), y LoopBack ignora esos params y
        // devuelve TODO. El filtro debe viajar como JSON string.
        const filter = JSON.stringify({ skip, limit, order });
        return this.notificationControllerService.notificationControllerFind(filter as any).pipe(
            map((response: any) =>
                Array.isArray(response) ? response : (response?.data ?? [])
            ),
            catchError((error) => {
                console.error('Error al obtener historial global:', error);
                return of([]);
            })
        );
    }

    /**
     * Contar todas las notificaciones en el sistema (Admin)
     */
    countAllNotifications(): Observable<number> {
        return this.notificationControllerService.notificationControllerCount().pipe(
            map((response: any) => response?.count || 0),
            catchError((error) => {
                console.error('Error al contar notificaciones globales:', error);
                return of(0);
            })
        );
    }
}
